/**
 * Origin Cell Renderer
 *
 * Renders the vulnerability origin(s) as chips.
 * Displays the tool(s) or integration(s) that discovered the vulnerability.
 */

import React, { useMemo } from 'react';

import { ChipsList } from '@/components/ChipsList';
import type { RiskWithVulnerability } from '@/hooks/useRisks';
import { useCellFilters } from '@/sections/vulnerabilities/components/cells/CellFiltersContext';
import { useAuth } from '@/state/auth';
import { useEntityFilters } from '@/utils/entityFilters.util';
import {
  getDisplayOriginTexts,
  mapOriginFilterValuesToDisplayLabels,
  orderOriginsByMatchedLabels,
} from '@/utils/originDisplay.util';

interface OriginCellProps {
  risk: RiskWithVulnerability;
}

/**
 * Memoized cell renderer for vulnerability origin column
 *
 * Displays one or more origins as brand-variant chips with Chariot rollup logic.
 * Origins are transformed by getDisplayOriginTexts before rendering:
 * - Native Chariot capabilities (subdomain-enumerator, portscanner, etc.) → "Chariot"
 * - Third-party integrations (github, aws, qualys, etc.) → Formatted integration name
 * - Manual imports (emails ending in @praetorian.com) → "Manual" (Praetorian users) / "Praetorian" (customers)
 * - Multiple origins are deduplicated (e.g., two native sources → one "Chariot" chip)
 *
 * Features:
 * - Shows the first origin as a chip with a "+N" overflow (all origins listed in the tooltip)
 * - Uses brand variant for consistent styling
 * - Handles empty origins gracefully
 * - Backward compatible with legacy risk.source field
 *
 * @param risk - The vulnerability/risk with origins array or legacy source field
 * @returns ChipsList component with origin chips or dash if no origins
 */
export const OriginCell: React.FC<OriginCellProps> = React.memo(({ risk }) => {
  // Get source filter options for Chariot group detection
  const { sortedOptions: sourceFilterOptions } = useEntityFilters(
    'Vulnerability',
    'vuln-table'
  );

  const { isPraetorianUser } = useAuth();

  // Active origin filters (raw backend values) from the vulnerabilities table.
  // Note the singular `origin` key; values may include '@praetorian.com' (manual).
  const filters = useCellFilters();
  const activeOriginFilters = filters?.advancedFilters?.origin;

  // Use origins array if available, fallback to source for backward compatibility
  const rawOrigins = risk.origins?.length
    ? risk.origins
    : risk.source
      ? [risk.source]
      : [];

  // Transform origins using the same rollup logic as the Assets table
  const displayOrigins = useMemo(
    () => getDisplayOriginTexts(rawOrigins, sourceFilterOptions),
    [rawOrigins, sourceFilterOptions]
  );

  // For vulnerabilities, Praetorian users see "Manual" instead of "Import";
  // customers and demo orgs see "Praetorian" for manually-added vulnerabilities.
  // The canonical ordering must run on these FINAL labels, and the mapped filter
  // labels get the SAME remap so filter-matching lines up (a filter value of
  // '@praetorian.com' → 'Import' → 'Manual'/'Praetorian' identically).
  const finalOrigins = useMemo(() => {
    const remapImport = (origin: string) => {
      if (origin !== 'Import') return origin;
      return isPraetorianUser ? 'Manual' : 'Praetorian';
    };

    const remappedOrigins = Array.from(
      new Set(displayOrigins.map(remapImport))
    );

    const matchedLabels =
      activeOriginFilters && activeOriginFilters.length > 0
        ? mapOriginFilterValuesToDisplayLabels(
            activeOriginFilters,
            sourceFilterOptions
          ).map(remapImport)
        : undefined;

    return orderOriginsByMatchedLabels(remappedOrigins, matchedLabels);
  }, [
    displayOrigins,
    isPraetorianUser,
    activeOriginFilters,
    sourceFilterOptions,
  ]);

  if (!finalOrigins.length) return <>-</>;

  return (
    <ChipsList
      items={finalOrigins}
      maxVisible={1} // First origin as a chip; the rest collapse into a "+N" overflow (all listed in the tooltip)
      variant="brand"
      className="max-w-[140px] min-w-[140px]"
      size="xs"
      data-testid="origin-chips"
    />
  );
});

OriginCell.displayName = 'OriginCell';
